import { useState, useEffect } from 'react';
import {
    TableRow,
    TableCell,
    Collapse
} from '@mui/material';

import Comment from './Comment';
import QuoteTable from './QuoteTable';
import { searchData } from '../services/api';
import { QUOTE_STATUS } from '../helpers/constants';

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_RETRIES = 5;
const EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes

export default function QuoteDetails(props) {
    const {
        open,
        comment,
        quotes = [],
        features
    } = props;

    const [currentQuote, setCurrentQuote] = useState(quotes);

    useEffect(() => {
        // Check local storage for processing quotes
        const processingMap = JSON.parse(localStorage.getItem('processingQuotes')) || {};

        const quoteId = quotes.quote_id;
        const expiryTime = processingMap[quoteId];

        if (quotes.status_code !== QUOTE_STATUS.OPEN) {
            if (expiryTime) cleanupLocalStorage(quoteId);
            setCurrentQuote(quotes);
            return;
        }

        if (expiryTime && Date.now() < expiryTime) {
            const isAlreadyPolling = currentQuote?.quote_id === quoteId && currentQuote?.status_code === QUOTE_STATUS.PROCESSING;
            
            if (!isAlreadyPolling) {
                startPolling(quoteId);
            }

            setCurrentQuote({
                ...quotes,
                status_code: QUOTE_STATUS.PROCESSING //4 = Processing flag for UI
            });
            return;
        }

        if (expiryTime) cleanupLocalStorage(quoteId);
        // set local state with synced quotes
        setCurrentQuote(quotes);
    }, [quotes]);

    const cleanupLocalStorage = (quoteId) => {
        const processingMap = JSON.parse(localStorage.getItem('processingQuotes')) || {};
        if (processingMap[quoteId]) {
            delete processingMap[quoteId];
            localStorage.setItem('processingQuotes', JSON.stringify(processingMap));
        }
    };

    const handleStatusUpdate = (newStatus, quoteId) => {
        setCurrentQuote((prev) => (
            {
                ...prev,
                status_code: newStatus
            }
        ));

        if (newStatus === QUOTE_STATUS.PROCESSING) { //4 = Processing
            //item in local storage will look like this: { "quoteId1": timestamp, "quoteId2": timestamp }
            const processingMap = JSON.parse(localStorage.getItem('processingQuotes')) || {};
            if (!processingMap[quoteId] || Date.now() > processingMap[quoteId]) {
                processingMap[quoteId] = Date.now() + EXPIRY_TIME;
                localStorage.setItem('processingQuotes', JSON.stringify(processingMap));
                //begin polling for status updates
                startPolling(quoteId);
            }
        }
    };

    const startPolling = (quoteId) => {
        const pollInterval = async (retryCount = 0) => {
            const processingMap = JSON.parse(localStorage.getItem('processingQuotes')) || {};
            const expiryTime = processingMap[quoteId];

            if (!expiryTime || Date.now() > expiryTime) {
                //timeout reached, stop polling
                cleanupLocalStorage(quoteId);
                setCurrentQuote(quotes);
                return;
            }

            if (retryCount >= MAX_RETRIES) {
                console.error(`Max retries reached for quote ID: ${quoteId}. Stopping polling.`);
                cleanupLocalStorage(quoteId);
                setCurrentQuote(quotes);
                return;
            }

            try {
                const response = await searchData('quotes', { documentNumber: quoteId });
                
                const quotesArray = Array.isArray(response) ? response : (response?.data || []);
                const latestQuote = quotesArray.find(q => q.quote_id === quoteId);

                if (latestQuote && latestQuote.status_code && latestQuote.status_code !== QUOTE_STATUS.OPEN) {
                    //status has changed, update and stop polling
                    setCurrentQuote((prev) => ({
                        ...prev,
                        status_code: latestQuote.status_code
                    }));
                    cleanupLocalStorage(quoteId);
                    return;
                }
                
                setTimeout(() => pollInterval(0), POLLING_INTERVAL); //poll every 5 seconds on success
            } catch (error) { //searchData will throw error on fetch issues
                console.error('Error while polling quote status:', error);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => pollInterval(retryCount + 1), POLLING_INTERVAL * 2); //poll every 10 seconds on error
                }
            }
        }
        pollInterval(0);
    }


    const buildQuoteTables = () => {
        if( !currentQuote || !currentQuote.options) return null;

        return currentQuote.options
            .sort((a, b) => a.option_no - b.option_no)
            .map((option) => {
                return (
                    <QuoteTable
                        key={option.option_no}
                        option={option}
                        quote={currentQuote}
                        status={currentQuote.status_code}
                        onStatusUpdate={handleStatusUpdate}
                        id={currentQuote.quote_id}
                        date={currentQuote.quote_dt}
                        expirationDate={currentQuote.expiration_dt}
                        features={features}
                    />
                )
            })
    }

    return (
        <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                <Collapse in={open} timeout="auto">
                    {buildQuoteTables()}
                <Comment comment={comment}/>
                </Collapse>
            </TableCell>
        </TableRow>
    )
}